import { create } from 'zustand'
import {
  changeAuthPassword,
  loadAuthSession,
  loginAuthSession,
  logoutAuthSession,
} from '@api/auth'
import { type AuthSession } from '@app-types/api/auth'
import { type AuthState } from '@app-types/stores/auth'

let pendingSession: Promise<AuthSession> | null = null

export const useAuthStore = create<AuthState>((set, get) => ({
  error: '',
  isLoading: false,
  session: null,

  changePassword: async (payload, csrfToken) => {
    const result = await changeAuthPassword(payload, csrfToken)

    if (result.success) {
      set((state) => {
        if (!state.session?.user) {
          return state
        }

        return {
          session: {
            ...state.session,
            user: {
              ...state.session.user,
              needsOldPassword: result.needsOldPassword,
            },
          },
        }
      })
    }

    return result
  },

  loadSession: async (options) => {
    const force = options?.force === true
    const currentSession = get().session
    if (currentSession && !force) {
      return currentSession
    }

    if (force) {
      pendingSession = null
      set({ error: '', isLoading: true, session: null })
    }

    if (!pendingSession) {
      set({ error: '', isLoading: true })
      pendingSession = loadAuthSession()
        .then((session) => {
          set({ error: '', isLoading: false, session })
          return session
        })
        .catch((error) => {
          set({
            error:
              error instanceof Error
                ? error.message
                : 'Не удалось загрузить данные пользователя',
            isLoading: false,
            session: null,
          })
          throw error
        })
        .finally(() => {
          pendingSession = null
        })
    }

    return pendingSession
  },

  loginSession: async (payload, csrfToken) => {
    const result = await loginAuthSession(payload, csrfToken)

    if (result.success) {
      pendingSession = null
      set({ error: '', isLoading: false, session: null })
    }

    return result
  },

  logoutSession: async (csrfToken: string) => {
    pendingSession = null
    set({ error: '', isLoading: true })

    try {
      await logoutAuthSession(csrfToken)
      set({ error: '', isLoading: false, session: null })
    } catch (error) {
      set({
        error:
          error instanceof Error
            ? error.message
            : 'Не удалось завершить сессию',
        isLoading: false,
      })
      throw error
    }
  },

  resetSession: () => {
    pendingSession = null
    set({ error: '', isLoading: false, session: null })
  },
}))

export function useAuthSession() {
  return useAuthStore((state) => state.session)
}

export function useRequiredAuthSession() {
  const session = useAuthSession()

  if (!session) {
    throw new Error('Auth session is required before rendering this component')
  }

  return session
}
